<template>
  <div id="app">
    <router-view />
    <div v-if="showInstallPrompt || showPushPrompt" class="app-prompts">
      <transition name="app-prompt">
        <div v-if="showPushPrompt" class="install-card push-card">
          <div class="install-card__icon">
            <i class="fas fa-bell" />
          </div>
          <div class="install-card__body">
            <strong>Recevoir les alertes Educ360</strong>
            <p>{{ pushPromptText }}</p>
            <p v-if="pushMessage" class="prompt-message">{{ pushMessage }}</p>
          </div>
          <div class="install-card__actions">
            <button type="button" :disabled="enablingPush" @click="enablePush">
              <i v-if="enablingPush" class="fas fa-spinner fa-spin" />
              {{ enablingPush ? 'Activation…' : 'Activer' }}
            </button>
            <button type="button" class="ghost" @click="dismissPush">Plus tard</button>
          </div>
        </div>
      </transition>

      <transition name="app-prompt">
        <div v-if="showInstallPrompt" class="install-card">
          <div class="install-card__icon">
            <i class="fas fa-mobile-alt" />
          </div>
          <div class="install-card__body">
            <strong>Installer Educ360</strong>
            <p v-if="isIos">Sur iPhone, touchez Partager puis “Sur l’écran d’accueil”.</p>
            <p v-else>Ajoutez Educ360 à votre écran d’accueil pour un accès plus rapide.</p>
          </div>
          <div class="install-card__actions">
            <button v-if="deferredPrompt" type="button" @click="installApp">Installer</button>
            <button type="button" class="ghost" @click="dismissInstall">Plus tard</button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { enablePushNotifications, supportsPushNotifications } from '@/utils/pushNotifications';
import { isIosDevice, isStandaloneDisplay } from '@/utils/installPrompt';

export default {
  name: 'App',
  data() {
    return {
      deferredPrompt: null,
      showInstallPrompt: false,
      showPushPrompt: false,
      enablingPush: false,
      pushMessage: '',
      isIos: false,
    };
  },
  mounted() {
    this.isIos = isIosDevice();
    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.dismissInstall);
    this.maybeShowIosInstall();
    this.maybeShowPushPrompt();
  },
  beforeDestroy() {
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', this.dismissInstall);
  },
  computed: {
    pushPromptText() {
      if (!supportsPushNotifications() && this.isIos && !isStandaloneDisplay()) {
        return 'Installez d’abord Educ360 sur l’écran d’accueil, puis ouvrez l’app installée pour activer les notifications.';
      }
      return 'Activez les notifications pour les actualités et informations importantes.';
    },
  },
  methods: {
    onBeforeInstallPrompt(event) {
      event.preventDefault();
      this.deferredPrompt = event;
      if (!localStorage.getItem('educ360InstallDismissed') && !isStandaloneDisplay()) {
        this.showInstallPrompt = true;
      }
    },
    maybeShowIosInstall() {
      if (!this.isIos || isStandaloneDisplay() || localStorage.getItem('educ360InstallDismissed')) return;
      window.setTimeout(() => {
        this.showInstallPrompt = true;
      }, 1600);
    },
    async installApp() {
      if (!this.deferredPrompt) return;
      this.deferredPrompt.prompt();
      await this.deferredPrompt.userChoice.catch(() => undefined);
      this.deferredPrompt = null;
      this.dismissInstall();
    },
    dismissInstall() {
      this.showInstallPrompt = false;
      localStorage.setItem('educ360InstallDismissed', 'true');
    },
    maybeShowPushPrompt() {
      if (!supportsPushNotifications() && !this.isIos) return;
      if (localStorage.getItem('pushNotificationsEnabled') === 'true') return;
      if (localStorage.getItem('educ360PushDismissed')) return;
      window.setTimeout(() => {
        this.showPushPrompt = true;
      }, 2600);
    },
    async enablePush() {
      if (!supportsPushNotifications()) {
        this.pushMessage = this.isIos && !isStandaloneDisplay()
          ? 'Sur iPhone, les notifications s’activent depuis l’app installée sur l’écran d’accueil.'
          : 'Ce navigateur ne prend pas encore en charge les notifications push.';
        return;
      }
      this.enablingPush = true;
      this.pushMessage = '';
      try {
        const res = await enablePushNotifications();
        if (res.ok) {
          this.showPushPrompt = false;
        } else if (res.reason === 'not_configured') {
          this.pushMessage = 'Les notifications ne sont pas encore configurées côté serveur.';
        } else if (res.reason === 'push_service_error' || res.reason === 'invalid_vapid_key') {
          this.pushMessage = 'Firebase Push n’a pas pu créer l’abonnement. Vérifiez la clé VAPID Web Push dans Firebase.';
        } else if (res.reason === 'native_push_failed') {
          this.pushMessage = 'Le navigateur refuse l’abonnement Push. Vérifiez le navigateur, le mode privé, ou les services Google/Push.';
        } else if (res.reason === 'service_worker_failed') {
          this.pushMessage = 'Le service worker n’a pas pu être enregistré. Rechargez la page puis réessayez.';
        } else if (res.reason === 'denied') {
          this.pushMessage = 'Les notifications sont bloquées dans votre navigateur.';
        } else {
          this.pushMessage = 'Impossible d’activer les notifications sur cet appareil.';
        }
      } catch (e) {
        console.error(e);
        this.pushMessage = 'Activation impossible pour le moment.';
      } finally {
        this.enablingPush = false;
      }
    },
    dismissPush() {
      this.showPushPrompt = false;
      localStorage.setItem('educ360PushDismissed', 'true');
    },
  },
}
</script>

<style scoped>
.app-prompts {
  bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  left: 50%;
  max-width: min(720px, calc(100vw - 24px));
  position: fixed;
  transform: translateX(-50%);
  width: max-content;
  z-index: 80;
}
.install-card {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #dbe7e5;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.16);
  display: grid;
  gap: 12px;
  grid-template-columns: auto 1fr auto;
  padding: 14px;
  width: max-content;
  max-width: 100%;
}
.install-card__icon {
  align-items: center;
  background: #ccfbf1;
  border-radius: 10px;
  color: #0f766e;
  display: flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}
.install-card__body strong {
  color: #111827;
  display: block;
  font-size: 14px;
}
.install-card__body p {
  color: #6b7280;
  font-size: 13px;
  margin: 3px 0 0;
}
.install-card__actions {
  display: flex;
  gap: 8px;
}
.install-card__actions button {
  background: #0f766e;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  min-height: 38px;
  padding: 0 12px;
}
.install-card__actions button:disabled {
  opacity: 0.65;
}
.install-card__actions .ghost {
  background: #f3f4f6;
  color: #374151;
}
.prompt-message {
  color: #b45309 !important;
}
.app-prompt-enter-active,
.app-prompt-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.app-prompt-enter,
.app-prompt-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
@media (max-width: 640px) {
  .app-prompts {
    width: calc(100vw - 24px);
  }
  .install-card {
    grid-template-columns: auto 1fr;
    width: 100%;
  }
  .install-card__actions {
    grid-column: 1 / -1;
    justify-content: stretch;
  }
  .install-card__actions button {
    flex: 1;
  }
}
</style>
